import * as Url from "url-parse"
import { isEmpty } from "lodash"
import { Service } from "./Service"
import { NavigationParam } from "../models/navigationParam"

class WebNavigationParamService extends Service implements NavigationParam {
    private static instance: WebNavigationParamService

    constructor() {
        super()
        // set le nom du service
        this.name = "NavigationParamService"
        // ajoute toute les methodes du service au tableau de mapping
        this.serviceMap = []
    }

    /**
     * sluggify Transforme une chaine de caractaire "testString" en "test-string"
     * et enlève tous les caractaires non pris en charges
     * @param s chaine à transformer
     */
    private sluggify(s: string): string {
        let result = s
        result = result.replace(
            /[A-Z]/g,
            (letter) => `-${letter.toLowerCase()}`
        )
        result = result.replace(/^\s+|\s+$/g, "") // trim
        result = result.toLowerCase()

        const from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;"
        const to = "aaaaeeeeiiiioooouuuunc------"
        for (let i = 0, l = from.length; i < l; i += 1) {
            result = result.replace(
                new RegExp(from.charAt(i), "g"),
                to.charAt(i)
            )
        }

        return result
            .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
            .replace(/\s+/g, "-") // collapse whitespace and replace by -
            .replace(/-+/g, "-") // collapse dashes
    }

    /**
     * getUrlHash parse l'URL et renvoie le hash qu'il contient
     */
    private getUrlHash(): string {
        const url = Url.default(window.location.href, true)
        // suppression du #
        return url.hash.replace("#", "")
    }

    /**
     * toClassName Transforme une chaine de caractaire "test-string" en "testString"
     * @param s chaine à transformer
     */
    private toClassName(s: string): string {
        return s.replace(
            /-[a-z0-9]/g,
            (letter: string) => `${letter.replaceAll("-", "").toUpperCase()}`
        )
    }

    /**
     * queryParamsObjectToString prend on object de params {param1: xxx, params2: yyy}
     * et le transforme en queryString param1=xxx&params2=yyy
     */
    private queryParamsObjectToString(object: any): string {
        let result = ""
        if (object) {
            const objectKeys = Object.keys(object)
            if (objectKeys.length) {
                result = ""
                objectKeys.forEach((key, index) => {
                    if (
                        object[key] &&
                        object[key] !== "" &&
                        (!isEmpty(object[key]) ||
                            typeof object[key] === "number" ||
                            typeof object[key] === "string" ||
                            typeof object[key] === "boolean")
                    ) {
                        result += `${key}=${object[key]}&`
                    }
                })
                // suppr du dernier &
                if (result[result.length - 1] === "&") {
                    result = result.substring(0, result.length - 1)
                }
            }
        }

        return result
    }

    /**
     * Récuperation de l'instance
     */
    public static getInstance(): WebNavigationParamService {
        if (!this.instance) {
            this.instance = new WebNavigationParamService()
        }
        return this.instance
    }

    public set(pageName: string, object?: unknown) {
        window.location.hash = `${this.sluggify(pageName)}${
            object && !isEmpty(object)
                ? `?${this.queryParamsObjectToString(object)}`
                : ""
        }`
    }

    /**
     * parseUrl parse l'url et renvoie un objet contenant toute les paramètres
     */
    public get(urlHash?: string): {
        pageName: string
        urlPageName: string
        params: any
    } {
        // on récupère le hash
        const hashSub = !urlHash ? this.getUrlHash() : urlHash.replace("#", "")
        // On transforme l'url hash en url standard ex. "test?attr=2" -> "http://base/test?attr=2"
        // NOTE: en mode debug React Native, l'implémentation de `URL` peut être partielle (ex: `URL.search` non implémenté).
        // On utilise `url-parse` (déjà présent) pour assurer un parsing stable.
        const normalizedHashSub = hashSub.startsWith("/")
            ? hashSub
            : `/${hashSub}`
        const parsedUrl: any = Url.default(
            `http://localhost:8080${normalizedHashSub}`,
            true
        )
        const query: unknown = parsedUrl?.query

        // extraction des query params si présent
        let queryParamsObject: Record<string, unknown> = {}
        if (query && typeof query === "object" && !Array.isArray(query)) {
            queryParamsObject = Object.entries(
                query as Record<string, unknown>
            ).reduce((acc, [key, val]) => {
                // `url-parse` peut retourner des valeurs string | string[] | undefined selon les cas
                if (typeof val === "string") {
                    acc[key] = decodeURIComponent(val)
                } else {
                    acc[key] = val
                }
                return acc
            }, {} as Record<string, unknown>)
        } else if (typeof query === "string" && query !== "") {
            // Fallback: si la lib retourne une query sous forme de string, on la parse à la main
            const hashes: string[] = query.split("&")
            queryParamsObject = hashes.reduce<Record<string, unknown>>(
                (params, hashStr) => {
                    const [key, val] = hashStr.split("=")
                    if (!key) return params
                    return Object.assign(params, {
                        [key]: val ? decodeURIComponent(val) : "",
                    })
                },
                {} as Record<string, unknown>
            )
        }

        // on récupère le pathName qui est le nom de la page en enlevant les possibles /
        const urlPageName: string = parsedUrl.pathname?.replaceAll
            ? parsedUrl.pathname.replaceAll("/", "")
            : parsedUrl.pathname.replace(/\//g, "")

        return {
            pageName: this.toClassName(urlPageName),
            urlPageName,
            params: queryParamsObject,
        }
    }

    /**
     * Supprimer entièrement toute les infos dans le hash de l'URL
     */
    public clear(): void {
        window.history.replaceState(
            "",
            document.title,
            window.location.pathname + window.location.search
        )
    }
}

export default WebNavigationParamService
